/**
 * Modal that lists every run in the event (minus the primary) so the
 * spectator can pick a vehicle to compare. After pick, BrowseStateView
 * projects the chosen run onto the raceline + mounts a secondary
 * player seeded to the equivalent moment.
 */

import React, { useMemo } from 'react';
import { X } from 'lucide-react';

import type { EnrollmentRow, Run } from '../services/fleet';

interface CompareRunPickerProps {
  open: boolean;
  runs: Run[];
  enrollments: EnrollmentRow[];
  excludeRunId: string | null;
  busy: boolean;
  error: string | null;
  onPick: (run: Run) => void;
  onClose: () => void;
}

const CompareRunPicker: React.FC<CompareRunPickerProps> = ({
  open,
  runs,
  enrollments,
  excludeRunId,
  busy,
  error,
  onPick,
  onClose,
}) => {
  const enrollmentByDevice = useMemo(() => {
    const m = new Map<string, EnrollmentRow>();
    for (const e of enrollments) m.set(e.device_id, e);
    return m;
  }, [enrollments]);

  if (!open) return null;
  const visible = runs.filter((r) => r.run_id !== excludeRunId);

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Pick a vehicle to compare"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-[min(420px,90vw)] flex-col overflow-hidden rounded-lg border border-border-subtle bg-bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border-subtle px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Compare to…</div>
            <div className="text-xs text-fg-secondary">
              Pick a vehicle to load alongside the current feed.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-fg-secondary hover:bg-bg-elevated hover:text-fg"
            aria-label="Close compare picker"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {visible.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-fg-secondary">
              No other runs in this event yet.
            </div>
          ) : (
            <ul className="space-y-1">
              {visible.map((run) => {
                const enr = enrollmentByDevice.get(run.device_id);
                const driver = enr?.driver_name || run.device_id;
                const vehicle = enr?.vehicle || null;
                const duration = formatDuration(run.start_utc_ms, run.end_utc_ms);
                return (
                  <li key={run.run_id}>
                    <button
                      type="button"
                      onClick={() => onPick(run)}
                      disabled={busy}
                      className="flex w-full items-center gap-3 rounded-md border border-border-subtle px-3 py-2 text-left hover:border-accent hover:bg-bg-elevated disabled:opacity-50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-fg">{driver}</div>
                        {vehicle && (
                          <div className="truncate text-xs text-fg-secondary">{vehicle}</div>
                        )}
                        <div className="mt-1 font-mono text-[10px] text-fg-muted">
                          {run.sessions.length} session{run.sessions.length === 1 ? '' : 's'} · {duration}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {error && (
          <div className="border-t border-status-error/40 bg-status-error/10 px-3 py-2 text-xs text-status-error">
            {error}
          </div>
        )}
        {busy && (
          <div className="border-t border-border-subtle bg-bg-elevated px-3 py-2 text-xs text-fg-secondary">
            Loading projection…
          </div>
        )}
      </div>
    </div>
  );
};

function formatDuration(start: number | null, end: number | null): string {
  if (start == null) return '—';
  if (end == null) return 'in progress';
  const dur = Math.max(0, end - start) / 1000;
  if (dur < 60) return `${Math.round(dur)} s`;
  const m = Math.floor(dur / 60);
  const s = Math.round(dur % 60);
  return `${m} min ${s} s`;
}

export default CompareRunPicker;
